"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './animate_on_scroll.css'; // Import your CSS for styles


interface AnimateOnScrollInterface {
  children: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  style?: React.CSSProperties | undefined;
  delay?: number;
  duration?: number;
  transition?: string;
  transition_enter?: string;
  transition_exit?: string;
  once?: boolean;
  threshold?: number;
}

interface AnimateOnScrollContextValue {
  isVisible: boolean;
  isEngaged: boolean;
  duration: number;
  transition?: string;
  transition_enter?: string;
  transition_exit?: string;
  resetOnExit: boolean;
}

const AnimateOnScrollContext = createContext<AnimateOnScrollContextValue | null>(null);

const resolveTransitionName = (
  ...candidates: Array<string | undefined>
) => {
  for (const name of candidates) {
    if (name) {
      return name;
    }
  }
  return 'default';
};

export const AnimateOnScroll: React.FC<AnimateOnScrollInterface> = ({ 
  children, 
  as: Component = 'div',
  className = '', 
  style, 
  delay = 0, 
  duration = 0.5,
  transition = '', 
  transition_enter = '', 
  transition_exit = '', 
  once = true,
  threshold = 0.2,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isEngaged, setIsEngaged] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current) {
      return undefined;
    }

    const node = ref.current;
    let timeoutId: number | undefined;

    const handleVisibilityChange = (visible: boolean) => {
      if (visible) {
        timeoutId = window.setTimeout(() => {
          setIsEngaged(true);
          setIsVisible(true);
        }, delay);
      } else if (!once) {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        setIsVisible(false);
        setIsEngaged(false);
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          handleVisibilityChange(entry.isIntersecting);
        },
        { threshold }
      );

      observer.observe(node);

      return () => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        observer.disconnect();
      };
    }

    const legacyScrollHandler = () => {
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const visible = rect.top < windowHeight * (1 - threshold) && rect.bottom >= windowHeight * threshold;
      handleVisibilityChange(visible);
    };

    (window as Window).addEventListener('scroll', legacyScrollHandler, { passive: true });
    legacyScrollHandler();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener('scroll', legacyScrollHandler);
    };
  }, [delay, once, threshold]);

  const resulting_transition = isEngaged
    ? isVisible
      ? resolveTransitionName(transition_enter, transition)
      : resolveTransitionName(transition_exit, transition)
    : resolveTransitionName(transition_exit, transition);

  const contextValue = useMemo<AnimateOnScrollContextValue>(
    () => ({
      isVisible,
      isEngaged,
      duration,
      transition,
      transition_enter,
      transition_exit,
      resetOnExit: !once,
    }),
    [duration, isEngaged, isVisible, once, transition, transition_enter, transition_exit]
  );

  const Comp = Component as React.ElementType;
  
  return (
    <AnimateOnScrollContext.Provider value={contextValue}>
      <Comp 
        {...(typeof Component === 'string' ? { ref } : {})}
        style={{
          ...style, 
          transition: `all ${duration}s ease`,
        }}
        className={`${className} animate-on-scroll ${isVisible 
        ? `animate-on-scroll-viewport-visible`
        : `animate-on-scroll-viewport-hidden`
        } transition-${resulting_transition} `}>
        {children}
      </Comp>
    </AnimateOnScrollContext.Provider>
  );
};

interface AnimateOnScrollChildProps {
  children: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  duration?: number;
  transition?: string;
  transition_enter?: string;
  transition_exit?: string;
}

export const AnimateOnScrollChild: React.FC<AnimateOnScrollChildProps & Omit<AnimateOnScrollChildProps, 'children'>> = ({
  children,
  as: Component = 'div',
  className = '',
  style,
  delay = 0,
  duration,
  transition = '',
  transition_enter = '',
  transition_exit = '',
  ...props
}) => {
  const context = useContext(AnimateOnScrollContext);
  const [isChildVisible, setIsChildVisible] = useState(false);
  const [isChildEngaged, setIsChildEngaged] = useState(false);

  useEffect(() => {
    if (!context || typeof window === 'undefined') {
      return undefined;
    }

    let timeoutId: number | undefined;

    if (context.isVisible) {
      timeoutId = window.setTimeout(() => {
        setIsChildEngaged(true);
        setIsChildVisible(true);
      }, delay);
    } else if (context.resetOnExit) {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setIsChildVisible(false);
      setIsChildEngaged(false);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [context, delay]);

  if (!context) {
    const Comp = Component as React.ElementType;
    return (
      <Comp {...props} className={className} style={style}>
        {children}
      </Comp>
    );
  }

  const resolvedDuration = duration ?? context.duration;
  const transitionName = isChildEngaged
    ? isChildVisible
      ? resolveTransitionName(transition_enter, transition, context.transition_enter, context.transition)
      : resolveTransitionName(transition_exit, transition, context.transition_exit, context.transition)
    : resolveTransitionName(transition_exit, transition, context.transition_exit, context.transition);

  const Comp = Component as React.ElementType;
  
  return (
    <Comp
      {...props}
      className={`${className} animate-on-scroll ${isChildVisible
        ? 'animate-on-scroll-viewport-visible'
        : 'animate-on-scroll-viewport-hidden'
      } transition-${transitionName}`}
      style={{
        ...style,
        transition: `all ${resolvedDuration}s ease`,
      }}
    >
      {children}
    </Comp>
  );
};
